/* oxlint-disable jsx-a11y/no-noninteractive-tabindex */
// The line above is for the tabIndex={0} on the new-path portal wrapper — necessary to keep focus on the "current" dropdown for onClickOutside triggers
import React, { useCallback, useMemo } from 'react';
import classnames from 'clsx';
import ReactDOM from 'react-dom';

import style from './dropdown.module.css';
import { DROPDOWN_WRAPPER_CONTAINER } from '../../../../constants/element-ids';
import { usePositioning } from '../../../hooks/dropdown/use-positioning';
import { useClickOutside } from '../../../hooks/use-click-outside';

// @ts-expect-error less import

/**
 * @typedef {import('./dropdown-types.d.ts').DropdownOverlayComponent} DropdownOverlayComponent
 * @typedef {import('./dropdown-types.d.ts').ComputedStyle} ComputedStyle
 */

/**
 * @type {DropdownOverlayComponent}
 */
export const DropdownOverlay = React.forwardRef(
  (
    {
      children,
      closeWhenChildrenClicked,
      containerAllowedMeToClose,
      dropdownMenuShouldHaveWidthOfButton,
      isOpen,
      menuPosition,
      onClose,
      onKeyDown,
      isNested = false,
      targetRef,
    },
    ref,
  ) => {
    const child = /** @type {JSX.Element} */ (React.Children.only(children));
    const {
      position,
      ref: dropdownRef,
      style: dropdownStyle,
      windowBoundingBox: { height: windowBoundingBoxHeight },
    } = usePositioning(targetRef, menuPosition);

    const dropdownMenuStyle = useMemo(() => {
      const boundingBox = targetRef.current && targetRef.current.getBoundingClientRect();
      /**
       * @type {ComputedStyle}
       */
      const computedStyle = {
        ...dropdownStyle,
        width: !dropdownMenuShouldHaveWidthOfButton || !boundingBox ? 'auto' : boundingBox.width,
      };
      if (isNested) {
        computedStyle.maxHeight = windowBoundingBoxHeight - dropdownStyle.top;
        computedStyle.left = dropdownStyle.left + boundingBox.width * 2;
      }
      return computedStyle;
    }, [dropdownMenuShouldHaveWidthOfButton, dropdownStyle, isNested, targetRef, windowBoundingBoxHeight]);

    const handleDropdownClick = useCallback(
      (e) => {
        if (containerAllowedMeToClose && containerAllowedMeToClose(e)) {
          onClose();
        } else if (dropdownRef.current && dropdownRef.current.contains(e.target) && closeWhenChildrenClicked) {
          onClose();
        }
      },
      [closeWhenChildrenClicked, containerAllowedMeToClose, dropdownRef, onClose],
    );

    // hook-based click-outside detection.
    // markInteractionInside is wired to capture-phase React handlers on the portal wrapper,
    // signalling "inside" for any React-descendant interaction (portal-transparent).
    const { markInteractionInside } = useClickOutside(dropdownRef, onClose, { enabled: isOpen });

    let dropdownMenuWrapperNode = global.document.getElementById(DROPDOWN_WRAPPER_CONTAINER);

    if (!dropdownMenuWrapperNode) {
      dropdownMenuWrapperNode = global.document.createElement('div');
      dropdownMenuWrapperNode.id = DROPDOWN_WRAPPER_CONTAINER;
      dropdownMenuWrapperNode.setAttribute('data-ignore-root-focus', '');
      global.document.body.appendChild(dropdownMenuWrapperNode);
    }

    const dropdownContent = React.cloneElement(child, {
      className: classnames(child.props.className, style['drop-menu'], style[position]),
      ref: dropdownRef,
      style: dropdownMenuStyle,
      // if child is React component, data-position can be used as a prop to add custom styles/behavior according to dropdown position
      // passed as a custom data- attribute so that if child is a dom element no unknown attribute warning will be thrown
      'data-position': position,
    });

    if (isOpen) {
      return ReactDOM.createPortal(
        // tabIndex={0} so empty-space/label clicks inside the overlay
        // delegated focus to it (inside overlay.current) rather than falling to
        // document.body, which would fire DropdownMenu._onBlur → _onClose via the toggler.
        // onBlur/onFocus intentionally omitted: the toggler's onBlur is the only blur path.
        <div
          ref={ref}
          className='dropdown-menu-top-level-wrapper'
          style={dropdownMenuStyle}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onClick={handleDropdownClick}
          onMouseDownCapture={markInteractionInside}
          onTouchStartCapture={markInteractionInside}
        >
          {dropdownContent}
        </div>,
        dropdownMenuWrapperNode,
      );
    }

    return null;
  },
);

DropdownOverlay.displayName = 'DropdownOverlay';
